import React, { useEffect, useState } from 'react';
import Page from '@/model/Page';
import api, { AxiosError } from '@/services/api';
import { toast } from 'react-toastify';
import { useDialog } from '@/hooks/useDialog';
import useForm from '@/hooks/useForm';
import validators from '@/helpers/validators';
import Event from '@/model/Event';
import { useConfirmDelete } from '@/hooks/useConfirmDelete';
import { FormInputName, ShouldShowModal, States } from '../../types';
import { FinishedEventsContainer } from './ui';
import { CloneContent } from '../../components/CloneContent';

export const FinishedEventsScreen: React.FC = (): JSX.Element => {
  const [state, setState] = useState<States>(States.default);
  const [page, setPage] = useState<Page<Event, Event>>({
    page: 1,
    pageSize: 10,
    sort: 'startDate',
    order: 'DESC',
    total: 1,
  });
  const [shouldShowModal, setShouldShowModal] = useState<ShouldShowModal>(ShouldShowModal.filter);
  const [menuList, setMenuList] = useState<string[]>([]);

  const { title, visible, onChangeTitle, onToggle } = useDialog();
  const confirmDelete = useConfirmDelete();

  const handleOnMountEventMenu = async (): Promise<void> => {
    try {
      setState(States.loading);
      const { data } = await api.get<string[]>('/event/menu');
      if (data && data.length > 0) {
        setMenuList(data);
      }
    } catch (error) {
      const err = error as AxiosError;
      if (err.response?.status !== 401) {
        toast.error(err.message);
      }
    } finally {
      setState(States.default);
    }
  };

  const handleOnPage = async (pageData: Page<Event, Event>): Promise<void> => {
    try {
      setState(States.loading);
      const { data } = await api.post<Page<Event, Event>>('/event/page/finished', pageData);
      if (data) {
        setPage(data);
      }
    } catch (error) {
      const err = error as AxiosError;
      if (err.response?.status !== 401) {
        toast.error(err.message);
      }
    } finally {
      setState(States.default);
    }
  };

  const { formData, formErrors, onChangeFormInput, isFormValid, resetForm } = useForm({
    initialData: {
      filterSearch: '',
      inputSearch: '',
    },
    validators: {
      inputSearch: [validators.required],
    },
  });

  const handleOnClearFilter = async (): Promise<void> => {
    resetForm();
    const newPage: Page<Event, Event> = {
      page: 1,
      pageSize: 10,
      sort: 'startDate',
      order: 'DESC',
      total: 1,
    };
    await handleOnPage(newPage);
  };

  const handleOnFilter = async (): Promise<void> => {
    if (isFormValid()) {
      const newPage: Page<Event, Event> = {
        page: 1,
        pageSize: 10,
        entity: {
          name: formData[FormInputName.inputSearch],
        } as Event,
        sort: 'startDate',
        order: 'DESC',
        total: 1,
      };
      onToggle();

      await handleOnPage(newPage);
    }
  };

  const handleOnClose = (): void => confirmDelete.hide();

  const handleCloneEvent = async (eventId: string): Promise<void> => {
    try {
      setState(States.loading);
      handleOnClose();
      await api.post(`event/copy/${eventId}`);
      toast.success('Evento clonado com sucesso!');
    } catch {
      toast.warn('Evento clonando, este processo pode demorar alguns minutos!');
    } finally {
      setState(States.default);
    }
  };

  const handleOnShowCloneEvent = (eventId: string): void => {
    let eventTitle = '';
    if (page && page.list && page.list.length > 0) {
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < page.list.length; i++) {
        if (page.list[i].id === eventId) {
          eventTitle = page.list[i].name;
          break;
        }
      }
    }

    confirmDelete.show({
      title: '',
      children: <CloneContent title={eventTitle} />,
      actions: [
        {
          title: 'Não, quero manter',
          theme: 'noneBorder',
          onClick: (): void => handleOnClose(),
        },
        {
          title: 'Sim, quero clonar',
          onClick: async () => {
            handleCloneEvent(eventId);
          },
        },
      ],
    });
  };

  const handleOnChangePageSize = async (newSize: number): Promise<void> => {
    const newPage: Page<Event, Event> = {
      page: 1,
      pageSize: newSize,
      sort: 'startDate',
      order: 'DESC',
      total: 1,
    };
    await handleOnPage(newPage);
  };

  const handleOnShouldShowModal = ({
    value,
    newTitleModal,
  }: {
    value: ShouldShowModal;
    newTitleModal: string | React.ReactNode;
  }): void => {
    setShouldShowModal(value);
    onChangeTitle(newTitleModal);
    onToggle();
  };

  const handleOnPaginationChange = async (newPageNumber: number): Promise<void> => {
    const newPage: Page<Event, Event> = {
      ...page,
      page: newPageNumber,
    };
    await handleOnPage(newPage);
  };

  useEffect(() => {
    handleOnMountEventMenu();
    handleOnPage(page);
  }, []);
  return (
    <FinishedEventsContainer
      state={state}
      title={title}
      visible={visible}
      onToggle={onToggle}
      shouldShowModal={shouldShowModal}
      onClearFilter={handleOnClearFilter}
      onFilter={handleOnFilter}
      onClone={handleOnShowCloneEvent}
      onChangePageSize={handleOnChangePageSize}
      page={page}
      menuList={menuList}
      onShouldShowModal={handleOnShouldShowModal}
      onPaginationChange={handleOnPaginationChange}
      formData={formData}
      formErrors={formErrors}
      onChangeFormInput={onChangeFormInput}
    />
  );
};
